/**
 * Variant C — selection tree + resolution primitives.
 *
 * Spec 23 P10.1. The unified Selection picker (Spec 19 v2) renders
 * sections grouped by gate, auto-resolves parent/dep implications,
 * and shows a running time estimate. This module owns the grammar:
 *   - ``SelectionNode`` — per-section data the picker renders
 *   - ``buildSelectionTree`` — DocTypeConfig → flat node list
 *   - ``resolveSelection`` — checked → {checked, implied, est}
 *   - ``groupByGate`` — flat list → per-gate packets (contiguous runs)
 *   - ``estMinutes`` — pure size→minutes mapping
 *
 * SRP: pure data functions. No React, no Office.js, no fetch. The
 * shell / LoomSelection consume these outputs and render; tests can
 * pin down every branch without jsdom.
 *
 * # DECISION: tree shape is FLAT (sections as peers), with
 * #   ``parent`` + ``indent`` metadata for rendering — not a nested
 * #   recursive structure.
 * # WHY: mirrors the backend ``skeleton: [string, string, number][]``
 * #      1:1, which is itself flat. A nested shape would require a
 * #      lossy conversion (what if a skeleton has §9.3.2 but no §9.3?
 * #      real YAMLs do this). Flat + parent-id preserves every
 * #      backend configuration without invention. The design
 * #      prototype uses the same shape.
 * # DATE: 2026-04-22
 *
 * # DECISION: ``deps`` default to [] in v1 (no backend signal).
 * # WHY: the planner's inter-module dependency data (Spec 11) is not
 * #      exposed on the pre-plan config endpoint today. Once we
 * #      expose a ``shared_retrievals``-flavoured pre-plan peek, the
 * #      tree builder will populate deps; today the resolver still
 * #      auto-includes parents (§9.3 → §9), which is the most common
 * #      implied-inclusion writers need. Empty deps means the
 * #      resolver is a pure parent-closure for now — still
 * #      meaningful, still testable.
 * # DATE: 2026-04-22
 *
 * # DECISION: parent inference is string-prefix on section numbering.
 * # WHY: ICH E3, ICH E2C, MEDDEV all use dotted section numbers. §9.3
 * #      has parent §9 iff §9 is also in the tree. Pure syntactic
 * #      rule — no doctype branching, works for any dotted-number
 * #      regulatory skeleton. Edges that don't fit the rule (Title
 * #      page "1" etc.) just have no parent, same as a top-level
 * #      section.
 * # DATE: 2026-04-22
 */

import type { DocTypeConfig } from "../api/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Write-strategy preview — mirrors backend's ``insertion_mode``.
 *  "live" = section-by-section Word writes; "batched" = python-docx
 *  assembles on completion, writer opens the doc at the end. */
export type WriteStrategy = "live" | "batched";

/** Section size — drives time estimate + optional visual emphasis. */
export type NodeSize = "s" | "m" | "l";

/** One row in the picker. Flat list; ``parent`` + ``indent`` express
 *  hierarchy for rendering. */
export interface SelectionNode {
  /** Section number as a display string — "9.3", "10.1", "Title". */
  n: string;
  /** Human-readable section title. */
  t: string;
  /** Gate key (configure / assemble / finalize, or whatever the
   *  doctype config declares). Always populated — sections without
   *  a gate mapping are dropped from the tree (can't render without
   *  a packet to live in). */
  gate: string;
  /** Rough size bucket — 's' (~1 min), 'm' (~3 min), 'l' (~8 min). */
  size: NodeSize;
  /** Ids of sections this one depends on. Resolver auto-includes
   *  them when this is checked. Empty in v1 (see header). */
  deps?: string[];
  /** Parent section id, if this is a child under a dotted hierarchy
   *  (§9.3's parent is §9). Resolver auto-includes it. */
  parent?: string;
  /** Render indent level. Default 0 (top-level); 1 for first-level
   *  children. Derived from parent presence; components can use
   *  this directly to compute padding. */
  indent?: number;
  /** Whether this node owns a drafting instruction (a backend module).
   *  false = pure grouping header: it appears purely to group its
   *  instructed descendants and to offer a cascade bulk-toggle. A
   *  header renders a derived tri-state checkbox and is NEVER a member
   *  of the checked/implied sets, the time estimate, or the commit
   *  payload. Absent ⇒ true — every existing draftable node is
   *  unaffected. */
  drafts?: boolean;
}

/** Gate metadata mirrored from the doctype config. The picker needs
 *  name + display label; a color comes from the token palette in
 *  the rendering layer (keeping this module pure data). */
export interface GateMeta {
  /** Backend key — "configure" / "assemble" / "finalize" etc. */
  name: string;
  /** Human-readable label for the packet header. */
  displayName: string;
}

/** One contiguous run of same-gate nodes in tree order. */
export interface GateGroup {
  gate: string;
  nodes: SelectionNode[];
}

/** Output of ``resolveSelection`` — what the picker needs to render
 *  each node correctly. */
export interface ResolvedSelection {
  /** Ids the writer explicitly ticked. */
  checkedSet: Set<string>;
  /** Ids auto-included by dependency / parent closure. Not in
   *  ``checkedSet`` — the two sets are disjoint so callers can test
   *  checked-vs-implied without filtering. */
  impliedSet: Set<string>;
  /** Rough total time estimate in minutes for the union. Useful for
   *  the "selected: N · ~Mm" header copy. */
  totalEstMin: number;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Minutes estimate per size bucket. Source for the "~Xm" label in
 * rows and the running total in the picker header.
 */
export function estMinutes(node: SelectionNode): number {
  if (node.size === "l") return 8;
  if (node.size === "m") return 3;
  return 1;
}

/**
 * Bucket a word-count target into a size label. Thresholds from the
 * design prototype: ≥1500 words feels like an hour-scale section
 * ("l"), 500–1499 is a page-scale section ("m"), below 500 is a
 * boilerplate section ("s"). Pure + total.
 */
export function sizeFromWordTarget(target: number): NodeSize {
  if (target >= 1500) return "l";
  if (target >= 500) return "m";
  return "s";
}

/**
 * Derive a parent section number from a dotted id. "9.3" → "9";
 * "10.2.1" → "10.2"; "1" → undefined. Pure + total. We only return
 * a parent candidate — the caller must confirm the parent actually
 * exists in the tree before attaching (``buildSelectionTree``
 * handles that check).
 */
export function parentCandidate(id: string): string | undefined {
  const lastDot = id.lastIndexOf(".");
  if (lastDot === -1) return undefined;
  return id.slice(0, lastDot);
}

/**
 * Compute rendering depth = number of ancestors that exist in the
 * tree. §6.1.1 with §6.1 + §6 in the tree → depth 2. We walk the
 * WHOLE dotted chain and count only the ancestors present in
 * ``allIds`` — a missing intermediate (e.g. §6.1 absent while §6 is
 * present) does NOT stop the walk, so the depth still reflects the
 * nearest existing ancestor the row attaches to (see the ``parent``
 * assignment in ``buildSelectionTree``). Keeping this consistent with
 * the nearest-ancestor ``parent`` walk means indent and parent never
 * disagree across gaps. Pure + bounded by dotted-id length.
 */
export function indentDepthFor(id: string, allIds: Set<string>): number {
  let depth = 0;
  for (let current = parentCandidate(id); current; current = parentCandidate(current)) {
    if (allIds.has(current)) depth++;
  }
  return depth;
}

/**
 * Build a set of "root" sections to display in the picker.
 *
 * Each module in the YAML defines multiple sections it encompasses (e.g., PSURD-02
 * covers ["2", "2.1", "2.2", ...] but only has one instruction for "2").
 * The picker should show only the first section of each module — the "root" section
 * that represents the whole module. Subsections are part of their parent module
 * and shouldn't appear as independent rows in the picker.
 *
 * This is pure YAML-driven: reuses the module structure to determine visibility,
 * not doctype-specific filtering.
 *
 */
function buildRootSectionSet(config: DocTypeConfig): Set<string> {
  const rootSections = new Set<string>();
  const modules = config.modules ?? {};

  // For each module, the first section in its sections array is the root
  for (const module of Object.values(modules)) {
    const sections = module.sections ?? [];
    if (sections.length > 0) {
      rootSections.add(sections[0]);
    }
  }

  return rootSections;
}

/**
 * Build the set of "grouping header" section ids — sections that own
 * NO drafting instruction of their own but ARE a dotted-number
 * ancestor of at least one instructed section. Example: ICH E3 §9
 * "Investigational Plan" owns no module, but §9.1/§9.2/§9.3.1… do, so
 * §9 is a header. The picker renders these as group rows whose
 * checkbox cascades to every instructed subsection beneath them.
 *
 * Pure. For each instructed id we walk ``parentCandidate`` upward and
 * collect every ancestor that (a) exists in the skeleton and (b) is
 * not itself instructed. Sections whose ancestors are all instructed,
 * or whose only descendants are non-instructed (the §14 and §16
 * appendix headers), never enter the set — the former have no header
 * ancestor, the latter are never reached from an instructed id. Nested
 * headers fall out naturally: §9.3.1 instructed → both §9.3 and §9 are
 * ancestors that own no module → both are headers.
 */
function buildHeaderSectionSet(skeletonIds: Iterable<string>, instructedIds: ReadonlySet<string>): Set<string> {
  const skeletonSet = new Set(skeletonIds);
  const headers = new Set<string>();
  for (const id of instructedIds) {
    for (let anc = parentCandidate(id); anc; anc = parentCandidate(anc)) {
      if (skeletonSet.has(anc) && !instructedIds.has(anc)) {
        headers.add(anc);
      }
    }
  }
  return headers;
}

// ---------------------------------------------------------------------------
// Tree builder
// ---------------------------------------------------------------------------

/**
 * Build a flat ``SelectionNode`` list from a backend ``DocTypeConfig``.
 *
 * Mapping:
 *   - For each skeleton entry [n, t, wordTarget] → one node.
 *   - ``gate``: look up the module that owns the section (via
 *     ``config.modules``), then the gate that owns that module (via
 *     ``config.gates``). Sections that can't be mapped to a gate are
 *     dropped with a warning — they can't render in a gate packet.
 *   - ``size`` from word target via ``sizeFromWordTarget``.
 *   - ``parent`` from dotted-id heuristic, confirmed against the
 *     same skeleton (so §9.3 only gets parent "9" if §9 is also in
 *     the tree).
 *   - ``indent`` = 1 when parent is present, else 0.
 *
 * Order preserved from ``skeleton`` — callers rely on this for the
 * gate grouping to produce contiguous runs.
 */
export function buildSelectionTree(config: DocTypeConfig): SelectionNode[] {
  // Section id → module id. Flatten config.modules for O(1) lookup.
  const sectionToModule = new Map<string, string>();
  for (const [moduleId, module] of Object.entries(config.modules ?? {})) {
    for (const sectionId of module.sections ?? []) {
      sectionToModule.set(sectionId, moduleId);
    }
  }

  // Module id → gate name. Same rationale.
  const moduleToGate = new Map<string, string>();
  for (const gate of config.gates ?? []) {
    for (const moduleId of gate.modules ?? []) {
      moduleToGate.set(moduleId, gate.name);
    }
  }

  // Determine which sections are "root" sections (first section of each module).
  const rootSections = buildRootSectionSet(config);

  const skeletonRows = config.skeleton ?? [];
  const skeletonIds = skeletonRows.map(([n]) => n);

  // Gate that owns a section's module, if any.
  const gateOfSection = (n: string): string | undefined => {
    const moduleId = sectionToModule.get(n);
    return moduleId ? moduleToGate.get(moduleId) : undefined;
  };

  // Instructed sections = root section of a module AND mapped to a gate.
  // These are the draftable rows. Everything the picker shows is either
  // one of these or a header that groups them.
  const instructedIds = new Set<string>(
    skeletonIds.filter((n) => rootSections.has(n) && gateOfSection(n) !== undefined),
  );
  // Grouping headers — ancestors of instructed sections that own no
  // instruction themselves. Rendered as cascade group rows (drafts:false).
  const headerIds = buildHeaderSectionSet(skeletonIds, instructedIds);
  // Union backs the parent/indent walks below so children attach across
  // gaps and headers nest under headers.
  const allIds = new Set<string>([...instructedIds, ...headerIds]);

  // A header's gate = the gate of the nearest instructed section that
  // FOLLOWS it in skeleton order, so ``groupByGate`` keeps the header in
  // the same contiguous packet its children begin. Computed with a single
  // reverse pass: while scanning backward, ``nextInstructedGate`` holds
  // the gate of the closest instructed section forward of the cursor.
  const headerGate = new Map<string, string>();
  let nextInstructedGate: string | undefined;
  for (let i = skeletonIds.length - 1; i >= 0; i--) {
    const id = skeletonIds[i];
    if (instructedIds.has(id)) {
      nextInstructedGate = gateOfSection(id);
    } else if (headerIds.has(id) && nextInstructedGate !== undefined) {
      headerGate.set(id, nextInstructedGate);
    }
  }

  const nodes: SelectionNode[] = [];
  const skippedWithoutGate: Array<{ n: string; moduleId?: string }> = [];
  for (const [n, t, wordTarget] of skeletonRows) {
    const isInstructed = instructedIds.has(n);
    const isHeader = headerIds.has(n);
    if (!isInstructed && !isHeader) {
      // Not draftable and not a grouping header. Root sections with no
      // gate mapping can't render — collect them for one summary warning
      // (repeated renders shouldn't spam the console). Non-root
      // subsections are folded into their module and skipped silently,
      // as before.
      if (rootSections.has(n)) skippedWithoutGate.push({ n, moduleId: sectionToModule.get(n) });
      continue;
    }
    const gate = isInstructed ? gateOfSection(n) : headerGate.get(n);
    if (!gate) {
      // Header with no resolvable gate (no following instructed section)
      // — shouldn't happen given detection guarantees a descendant, but
      // guard rather than render a gate-less row.
      skippedWithoutGate.push({ n, moduleId: sectionToModule.get(n) });
      continue;
    }
    // Attach to the NEAREST existing ancestor (not just the immediate
    // dotted parent) so §9.3.1 attaches to §9.3 when present, or to §9
    // across a gap. Keeps the cascade (``descendantsOf``) and indent
    // consistent. §6.1.1 with §6.1 and §6 present gets indent: 2.
    let attachedParent: string | undefined;
    for (let p = parentCandidate(n); p; p = parentCandidate(p)) {
      if (allIds.has(p)) {
        attachedParent = p;
        break;
      }
    }
    nodes.push({
      n,
      t,
      gate,
      size: sizeFromWordTarget(wordTarget),
      parent: attachedParent,
      indent: indentDepthFor(n, allIds),
      // Grouping headers own no module; mark them non-drafting so the
      // picker treats them as cascade rows only.
      ...(isHeader ? { drafts: false } : {}),
      // deps = [] by default — see module header DECISION.
    });
  }
  if (skippedWithoutGate.length > 0) {
    // eslint-disable-next-line no-console
    console.warn("[selection.buildSelectionTree] skipped sections with no gate mapping", {
      count: skippedWithoutGate.length,
      sections: skippedWithoutGate,
    });
  }
  return nodes;
}

/**
 * Gate metadata in the order the doctype config declares them.
 * Backs the packet-header rendering (label + ordering). Returned
 * directly so callers don't have to re-walk config.gates.
 */
export function buildGateMeta(config: DocTypeConfig): GateMeta[] {
  return (config.gates ?? []).map((g) => ({
    name: g.name,
    displayName: g.display_name,
  }));
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Given a tree + the writer's checked ids, compute the full
 * resolved selection: checked set (as given), implied set (parents
 * + deps transitively), and total estimate.
 *
 * Semantics:
 *   - An id in ``checked`` but not present in the tree is silently
 *     ignored (stale selection survives a doctype swap gracefully).
 *   - ``impliedSet`` is disjoint from ``checkedSet``. If a writer
 *     explicitly ticks §9 AND §9.3, §9 stays in ``checkedSet`` and
 *     is NOT in ``impliedSet`` (even though §9.3 would imply it).
 *   - Closure is transitive: §9.3 implies §9; if §9 itself has
 *     deps, those join the implied set too.
 *   - Termination: each id is added to impliedSet at most once;
 *     stack is bounded by tree size.
 */
export function resolveSelection(tree: SelectionNode[], checked: Iterable<string>): ResolvedSelection {
  const byId = new Map<string, SelectionNode>(tree.map((n) => [n.n, n]));
  const checkedSet = new Set<string>();
  for (const id of checked) if (byId.has(id)) checkedSet.add(id);

  const impliedSet = new Set<string>();
  // ``seen`` guards against re-pushing an id we've already processed —
  // needed because grouping headers are pushed onto the stack (to walk
  // THROUGH them to real ancestors) without ever entering impliedSet, so
  // the impliedSet membership check alone wouldn't stop a re-push loop.
  const seen = new Set<string>(checkedSet);
  const stack: string[] = [...checkedSet];
  while (stack.length > 0) {
    const id = stack.pop() ?? "";
    const node = byId.get(id);
    if (!node) continue;
    // Parent inclusion — pull the parent into implied UNLESS it's a
    // non-drafting grouping header (headers are derived tri-state, never
    // "deps"). Either way, keep walking through the parent so a real
    // drafting ancestor higher up is still reached.
    if (node.parent) {
      const parentNode = byId.get(node.parent);
      const parentDrafts = parentNode ? parentNode.drafts !== false : true;
      if (parentDrafts && !checkedSet.has(node.parent)) {
        impliedSet.add(node.parent);
      }
      if (!seen.has(node.parent)) {
        seen.add(node.parent);
        stack.push(node.parent);
      }
    }
    // Dep inclusion — same rule per dep.
    for (const dep of node.deps ?? []) {
      if (!checkedSet.has(dep) && !seen.has(dep)) {
        impliedSet.add(dep);
        seen.add(dep);
        stack.push(dep);
      }
    }
  }

  let totalEstMin = 0;
  for (const node of tree) {
    // Headers contribute no drafting time — they own no instruction.
    if (node.drafts === false) continue;
    if (checkedSet.has(node.n) || impliedSet.has(node.n)) {
      totalEstMin += estMinutes(node);
    }
  }

  return { checkedSet, impliedSet, totalEstMin };
}

// ---------------------------------------------------------------------------
// Gate grouping
// ---------------------------------------------------------------------------

/**
 * Group a flat tree into contiguous same-gate runs, preserving tree
 * order. Contiguity matters — a skeleton that interleaves gates
 * (rare but valid) produces multiple groups for the same gate, in
 * the order they appear. The picker renders each packet sequentially,
 * so this preserves the writer's expected top-to-bottom reading.
 */
export function groupByGate(tree: SelectionNode[]): GateGroup[] {
  const out: GateGroup[] = [];
  let current: GateGroup | null = null;
  for (const node of tree) {
    if (!current || current.gate !== node.gate) {
      current = { gate: node.gate, nodes: [] };
      out.push(current);
    }
    current.nodes.push(node);
  }
  return out;
}

/**
 * Count top-level (indent=0) nodes in a tree — used by the "N
 * selected" header + the gate-header select-all predicate.
 * Extracted so both LoomSelection + tests share one definition.
 */
export function topLevelNodes(tree: SelectionNode[]): SelectionNode[] {
  return tree.filter((n) => !n.indent);
}

/**
 * All transitive descendant ids of a section, in tree order — §9
 * returns §9.1, §9.3, §9.3.1, …; the section itself is excluded.
 *
 * Backs the picker's downward cascade: ticking a root/parent section
 * auto-ticks every subsection under it. Walks the ``parent`` chain
 * (not a string prefix, which would let "1" claim "10"/"11"), the
 * same relation the resolver uses for the upward child→parent closure.
 */
export function descendantsOf(tree: SelectionNode[], id: string): string[] {
  const byId = new Map(tree.map((n) => [n.n, n]));
  const isDescendant = (n: SelectionNode): boolean => {
    for (let p = n.parent; p; p = byId.get(p)?.parent) {
      if (p === id) return true;
    }
    return false;
  };
  return tree.filter(isDescendant).map((n) => n.n);
}

// ---------------------------------------------------------------------------
// Section → module / gate mapping — bridges the picker's section-id
// vocabulary to the backend's module-id vocabulary expected by
// ``startRun`` and ``createPlan``.
// ---------------------------------------------------------------------------

/**
 * Given a DocTypeConfig + a set of section ids, return the set of
 * module ids that own any of those sections. Pure. Deterministic.
 * Used to map ``LoomSelectionCommit.resolvedSectionIds`` → the
 * ``module_ids`` parameter of ``startRun``.
 *
 * A section with no owning module is silently skipped — the caller's
 * tree builder already logged the warning when the section was
 * dropped from the tree (see ``buildSelectionTree``), so we don't
 * re-warn here.
 */
export function sectionIdsToModuleIds(config: DocTypeConfig, sectionIds: Iterable<string>): string[] {
  // Build the reverse index once per call. Callers that map the
  // same config repeatedly can memoise upstream; we keep this pure.
  const sectionToModule = new Map<string, string>();
  for (const [moduleId, module] of Object.entries(config.modules ?? {})) {
    for (const sectionId of module.sections ?? []) {
      sectionToModule.set(sectionId, moduleId);
    }
  }
  const out = new Set<string>();
  for (const sectionId of sectionIds) {
    const moduleId = sectionToModule.get(sectionId);
    if (moduleId) out.add(moduleId);
  }
  return [...out];
}

/**
 * Same idea, one level up: given a DocTypeConfig + module ids, return
 * the gate names that own any of those modules. Used to narrow
 * ``createPlan``'s ``gates`` parameter to only those the writer
 * actually wants drafted — avoids planning gates that won't run.
 */
export function moduleIdsToGateNames(config: DocTypeConfig, moduleIds: Iterable<string>): string[] {
  const moduleToGate = new Map<string, string>();
  for (const gate of config.gates ?? []) {
    for (const moduleId of gate.modules ?? []) {
      moduleToGate.set(moduleId, gate.name);
    }
  }
  const out = new Set<string>();
  for (const moduleId of moduleIds) {
    const gateName = moduleToGate.get(moduleId);
    if (gateName) out.add(gateName);
  }
  return [...out];
}
